import { ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flex } from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  accessTokenInfoAtom,
  splashImageInfoAtom,
  isQueryModeAtom,
  recTypeAtom,
  contractEnginesAtom,
} from "../../../store";
import {
  latestMimosysAtom,
  latestEmotionAtom,
  fetchLatestMimosysData,
  fetchLatestEmotions,
  fetchScoreSd,
  scoreSdAtom,
} from "../../../stores/mimosysStore";
import { ANALYSIS_ENGINE, APP_TYPE } from "../../../environments";
import { getSelectableEngineList } from "../../../pages/engines";
import { homeSplashDisplayAtom } from "../ResultHome/HomeAtoms";
import { SplashImage } from "../SplashImage";
import { getEmotionAverage } from "../../../engines/mimosysEngine";
import { sentryLog } from "../../../../sentry";
import { useToastMessage } from "../../../hooks/useToastMessage";

/*
 * Sphash表示
 */
export function Splash(): ReactElement {
  const [isExpired, setExpired] = useState(false);
  const [isApiCompleted, setApiCompleted] = useState(false);

  const splash = useRef(0);
  const navigate = useNavigate();
  const [splashImageInfo] = useAtom(splashImageInfoAtom);
  const [isQueryLogin] = useAtom(isQueryModeAtom);
  const [, setLatestMimosys] = useAtom(latestMimosysAtom);
  const [, setScoreSd] = useAtom(scoreSdAtom);
  const [, setMimosysEmotions] = useAtom(latestEmotionAtom);
  const [, setRecType] = useAtom(recTypeAtom);
  const [, setHomeSplashDisplay] = useAtom(homeSplashDisplayAtom);
  const [contractEngines] = useAtom(contractEnginesAtom);
  const toastMessage = useToastMessage();

  const [accessTokenInfo] = useAtom(accessTokenInfoAtom);
  if (accessTokenInfo === null) throw new Error("no token");

  /*
   * 2秒タイムアウト計測
   */
  useEffect(() => {
    const id = setTimeout(() => {
      setExpired(true);
    }, 2 * 1000);
    return () => {
      clearTimeout(id);
    };
  });

  /*
   * Mi1解析結果リスト取得
   */
  useEffect(() => {
    setRecType(0);
    const fetchData = async (): Promise<void> => {
      if (isQueryLogin) {
        // クエリログインなら何もしない
        setApiCompleted(true);
        return;
      }

      // 取得済なら何もしない
      if (0 < splash.current) return;

      splash.current += 1;
      try {
        const [latestMimosys, scoreSd] = await Promise.all([
          fetchLatestMimosysData(),
          fetchScoreSd(),
        ]);
        setLatestMimosys(latestMimosys);
        setScoreSd(scoreSd);

        if (APP_TYPE === "poc") {
          const latest = await fetchLatestEmotions();
          if (latest) {
            setMimosysEmotions(getEmotionAverage(latest.emotions));
          }
        }
      } catch (e) {
        sentryLog(e);
        toastMessage(e);
      } finally {
        setApiCompleted(true);
      }
    };
    fetchData();
  }, [
    setRecType,
    isQueryLogin,
    setLatestMimosys,
    setScoreSd,
    setHomeSplashDisplay,
    setMimosysEmotions,
    toastMessage,
  ]);

  /*
   * 遷移
   */
  useEffect(() => {
    if (isExpired && isApiCompleted) {
      let startTo = "";
      if (!isQueryLogin) {
        // クエリログインでは無い場合、ホーム画面に遷移
        setHomeSplashDisplay(false);
        return;
      }
      if (ANALYSIS_ENGINE) {
        startTo = `../engines/${ANALYSIS_ENGINE}/recording`;
      } else {
        const url_list = getSelectableEngineList(contractEngines);
        if (url_list.length == 1)
          startTo = "../engines/" + url_list[0] + "/recording";
        else startTo = "../engines";
      }
      // クエリログインの場合、録音開始の画面に遷移
      // navigate(startTo, { replace: true });
      navigate(startTo);
    }
  }, [
    isExpired,
    isApiCompleted,
    isQueryLogin,
    navigate,
    setHomeSplashDisplay,
    contractEngines,
  ]);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      height="100vh"
      width="100vw"
    >
      <motion.div
        style={{ opacity: 0, width: "100%", height: "100%" }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <SplashImage imageInfo={splashImageInfo} />
      </motion.div>
    </Flex>
  );
}
